import { useFrame, useThree } from '@react-three/fiber'
import { useEffect, useRef } from 'react'
import * as THREE from 'three'
import { useFootstepEffectTextures } from '../../effects/useFootstepEffectTextures'

export type BatteryObstacleLightningStrikeConfig = {
    center: THREE.Vector3
    halfExtents: THREE.Vector3
}

type ActiveLightningStrike = {
    age: number
    coreLines: THREE.Line[]
    glowLines: THREE.Line[]
    impactSprite: THREE.Sprite
    topSprite: THREE.Sprite
    impactScale: number
    topScale: number
    coreMaterials: THREE.LineBasicMaterial[]
    glowMaterials: THREE.LineBasicMaterial[]
    impactMaterial: THREE.SpriteMaterial
    topMaterial: THREE.SpriteMaterial
}

type BatteryObstacleLightningEffectProps = {
    consumeStrikeQueue: () => BatteryObstacleLightningStrikeConfig[]
}

const strikeSettings = {
    lifetime: 0.18,
    coreColor: '#f7ffb0',
    glowColor: '#d2ff2a',
    impactColor: '#efff9a',
    topColor: '#b9ff22',
    glowOpacity: 0.95,
    coreOpacity: 1,
    spriteOpacity: 0.8,
    topHeight: 4.2,
    horizontalJitter: 0.45,
    segments: 6,
    boltCount: 3,
    boltAngleJitter: 0.2,
    boltOffsetRadius: 0.22,
    impactScale: 1.9,
    topScale: 1.2,
}

const _start = new THREE.Vector3()
const _end = new THREE.Vector3()
const _point = new THREE.Vector3()
const _yawQuaternion = new THREE.Quaternion()
const _boltOffset = new THREE.Vector3()

export function BatteryObstacleLightningEffect({
    consumeStrikeQueue,
}: BatteryObstacleLightningEffectProps) {
    const { scene } = useThree()
    const activeStrikesRef = useRef<ActiveLightningStrike[]>([])
    const { dustTexture } = useFootstepEffectTextures()

    useEffect(() => {
        return () => {
            for (const strike of activeStrikesRef.current) {
                strike.coreLines.forEach((line) => scene.remove(line))
                strike.glowLines.forEach((line) => scene.remove(line))
                scene.remove(strike.impactSprite)
                scene.remove(strike.topSprite)
                strike.coreLines.forEach((line) => line.geometry.dispose())
                strike.glowLines.forEach((line) => line.geometry.dispose())
                strike.coreMaterials.forEach((material) => material.dispose())
                strike.glowMaterials.forEach((material) => material.dispose())
                strike.impactMaterial.dispose()
                strike.topMaterial.dispose()
            }
        }
    }, [scene])

    function createLightningPoints(
        config: BatteryObstacleLightningStrikeConfig,
        yaw: number,
        offsetRadius: number,
    ) {
        const points: THREE.Vector3[] = []
        const widthJitter = Math.max(
            config.halfExtents.x,
            config.halfExtents.z,
            strikeSettings.horizontalJitter,
        )

        _end.copy(config.center)
        _start.set(
            config.center.x,
            config.center.y + config.halfExtents.y + strikeSettings.topHeight,
            config.center.z,
        )
        _yawQuaternion.setFromAxisAngle(THREE.Object3D.DEFAULT_UP, yaw)
        _boltOffset.set(offsetRadius, 0, 0).applyQuaternion(_yawQuaternion)
        _start.add(_boltOffset)
        _end.add(_boltOffset)

        for (let index = 0; index <= strikeSettings.segments; index += 1) {
            const t = index / strikeSettings.segments
            _point.lerpVectors(_start, _end, t)

            if (index !== 0 && index !== strikeSettings.segments) {
                const taper = 1 - Math.abs(t - 0.5) * 1.35
                _point.x += (Math.random() * 2 - 1) * widthJitter * taper
                _point.z += (Math.random() * 2 - 1) * widthJitter * taper
            }

            points.push(_point.clone())
        }

        return points
    }

    function spawnStrike(config: BatteryObstacleLightningStrikeConfig) {
        const baseYaw = Math.random() * Math.PI * 2
        const coreLines: THREE.Line[] = []
        const glowLines: THREE.Line[] = []
        const coreMaterials: THREE.LineBasicMaterial[] = []
        const glowMaterials: THREE.LineBasicMaterial[] = []

        for (let boltIndex = 0; boltIndex < strikeSettings.boltCount; boltIndex += 1) {
            const boltYaw =
                baseYaw +
                (Math.PI * 2 * boltIndex) / strikeSettings.boltCount +
                (Math.random() * 2 - 1) * strikeSettings.boltAngleJitter
            const boltOffsetRadius =
                strikeSettings.boltCount <= 1
                    ? 0
                    : (Math.random() * 0.6 + 0.4) * strikeSettings.boltOffsetRadius
            const points = createLightningPoints(config, boltYaw, boltOffsetRadius)
            const coreGeometry = new THREE.BufferGeometry().setFromPoints(points)
            const glowGeometry = coreGeometry.clone()

            const glowMaterial = new THREE.LineBasicMaterial({
                color: strikeSettings.glowColor,
                transparent: true,
                opacity: strikeSettings.glowOpacity,
                blending: THREE.AdditiveBlending,
                depthWrite: false,
                toneMapped: false,
            })
            const coreMaterial = new THREE.LineBasicMaterial({
                color: strikeSettings.coreColor,
                transparent: true,
                opacity: strikeSettings.coreOpacity,
                depthWrite: false,
                toneMapped: false,
            })

            glowMaterials.push(glowMaterial)
            coreMaterials.push(coreMaterial)
            glowLines.push(new THREE.Line(glowGeometry, glowMaterial))
            coreLines.push(new THREE.Line(coreGeometry, coreMaterial))
        }

        const impactMaterial = new THREE.SpriteMaterial({
            map: dustTexture,
            color: strikeSettings.impactColor,
            transparent: true,
            opacity: strikeSettings.spriteOpacity,
            depthWrite: false,
            blending: THREE.AdditiveBlending,
            toneMapped: false,
        })
        const impactSprite = new THREE.Sprite(impactMaterial)
        impactSprite.position.copy(config.center)
        const impactScale = Math.max(
            strikeSettings.impactScale,
            config.halfExtents.length() * 1.35,
        )
        impactSprite.scale.setScalar(impactScale)

        const topMaterial = new THREE.SpriteMaterial({
            map: dustTexture,
            color: strikeSettings.topColor,
            transparent: true,
            opacity: strikeSettings.spriteOpacity * 0.8,
            depthWrite: false,
            blending: THREE.AdditiveBlending,
            toneMapped: false,
        })
        const topSprite = new THREE.Sprite(topMaterial)
        topSprite.position.set(
            config.center.x,
            config.center.y + config.halfExtents.y + strikeSettings.topHeight,
            config.center.z,
        )
        const topScale = strikeSettings.topScale
        topSprite.scale.setScalar(topScale)

        glowLines.forEach((line) => scene.add(line))
        coreLines.forEach((line) => scene.add(line))
        scene.add(impactSprite)
        scene.add(topSprite)

        activeStrikesRef.current.push({
            age: 0,
            coreLines,
            glowLines,
            impactSprite,
            topSprite,
            impactScale,
            topScale,
            coreMaterials,
            glowMaterials,
            impactMaterial,
            topMaterial,
        })
    }

    useFrame((_, delta) => {
        const safeDelta = Math.min(delta, 1 / 30)
        const queuedStrikes = consumeStrikeQueue()
        for (const strike of queuedStrikes) {
            spawnStrike(strike)
        }

        for (let index = activeStrikesRef.current.length - 1; index >= 0; index -= 1) {
            const strike = activeStrikesRef.current[index]
            strike.age += safeDelta
            const t = strike.age / strikeSettings.lifetime

            if (t >= 1) {
                strike.coreLines.forEach((line) => scene.remove(line))
                strike.glowLines.forEach((line) => scene.remove(line))
                scene.remove(strike.impactSprite)
                scene.remove(strike.topSprite)
                strike.coreLines.forEach((line) => line.geometry.dispose())
                strike.glowLines.forEach((line) => line.geometry.dispose())
                strike.coreMaterials.forEach((material) => material.dispose())
                strike.glowMaterials.forEach((material) => material.dispose())
                strike.impactMaterial.dispose()
                strike.topMaterial.dispose()
                activeStrikesRef.current.splice(index, 1)
                continue
            }

            const fade = Math.pow(1 - t, 1.5)
            strike.coreMaterials.forEach((material) => {
                material.opacity = strikeSettings.coreOpacity * fade
            })
            strike.glowMaterials.forEach((material) => {
                material.opacity = strikeSettings.glowOpacity * fade * 0.9
            })
            strike.impactMaterial.opacity = strikeSettings.spriteOpacity * fade
            strike.topMaterial.opacity = strikeSettings.spriteOpacity * fade * 0.7

            const pulse = 1 + t * 0.45
            strike.impactSprite.scale.setScalar(strike.impactScale * pulse)
            strike.topSprite.scale.setScalar(strike.topScale * pulse)
        }
    })

    return null
}

